import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Fade,
  Zoom
} from '@mui/material';
import { styled } from '@mui/material/styles';

const LoadingOverlayContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  backdropFilter: 'blur(4px)',
}));

const LoadingContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  maxWidth: 400,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  borderRadius: 16,
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  marginBottom: theme.spacing(3),
}));

const ProgressText = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

const CableDesignLoadingOverlay = ({ open, progress = 0 }) => {
  if (!open) return null;

  const steps = [
    'Analyzing your cable design...',
    'Preparing connector images...',
    'Generating cable diagram...',
    'Optimizing for production...',
    'Finalizing your custom cable...'
  ];

  // Adjust step timing for 18-second process
  // Each step should take roughly 3.6 seconds (18/5)
  const currentStep = Math.min(Math.floor(progress / 18), steps.length - 1);
  const currentStepText = steps[currentStep];

  return (
    <Fade in={open} timeout={300}>
      <LoadingOverlayContainer>
        <Zoom in={open} timeout={500}>
          <LoadingContent elevation={24}>
            <ProgressContainer>
              <CircularProgress
                size={80}
                thickness={4}
                variant="determinate"
                value={progress}
                sx={{
                  color: 'primary.main',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <ProgressText>
                {Math.round(progress)}%
              </ProgressText>
            </ProgressContainer>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                mb: 2,
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Designing Your Custom Cable
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 3,
                lineHeight: 1.6,
                fontWeight: 500,
              }}
            >
              {currentStepText}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              {steps.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: index <= currentStep ? 'primary.main' : 'grey.300',
                    transition: 'all 0.3s ease',
                    transform: index === currentStep ? 'scale(1.2)' : 'scale(1)',
                  }}
                />
              ))}
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mt: 2,
                fontStyle: 'italic',
                opacity: 0.8,
              }}
            >
              This may take a moment...
            </Typography>
          </LoadingContent>
        </Zoom>
      </LoadingOverlayContainer>
    </Fade>
  );
};

export default CableDesignLoadingOverlay;
